// src/pages/Appointments/AppointmentForm/appointmentFormHooks.js
import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom"; // ✅ Added useParams
import toast from "react-hot-toast";
import {
  adminCreateAppointmentThunk,
  rescheduleAppointmentThunk, // ✅ Added reschedule thunk
  getAppointmentDetailsThunk, // ✅ Added to fetch existing appointment
} from "../../../store/thunks/appointmentThunks";

// Default form structure
const DEFAULT_FORM_DATA = {
  userId: "",
  selectedCustomer: null,
  branchCode: "",
  branchId: "",
  storeCode: "",
  adminCode: "",
  type: "",
  selectedDate: "",
  selectedTime: "",
  scheduledDateTime: "",
  duration: 30,
  cartId: "",

  // ✅ Added for reschedule
  rescheduleReason: "",

  // Intent details
  intent: {
    purpose: "",
    occasion: "",
    occasionDate: "",
    forWhom: "",
    budget: {
      min: "",
      max: "",
      flexible: false,
    },
    preferences: {
      metals: [],
      styles: [],
      categories: [],
      weightRange: {
        min: "",
        max: "",
      },
    },
    customerNotes: "",
    specialRequests: "",
  },

  // Contact info (pre-filled from user)
  contactInfo: {
    name: "",
    phone: "",
    email: "",
  },
};

export const useAppointmentForm = (mode = "add") => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: appointmentId } = useParams(); // ✅ Get appointment ID from URL

  const { user } = useSelector((state) => state.auth);
  const { selectedBranch } = useSelector((state) => state.branch);
  const { cart } = useSelector((state) => state.cart);
  const { selectedAppointment, loadingDetails, error } = useSelector(
    (state) => state.appointment
  ); // ✅ Get selected appointment from Redux

  // ✅ Use refs to track if initial setup has been done
  const initializedRef = useRef(false);
  const branchSetRef = useRef(false);
  const cartSetRef = useRef(false);
  const appointmentLoadedRef = useRef(false); // ✅ Track if appointment data loaded

  // Form state
  const [formData, setFormData] = useState(() => {
    const initial = { ...DEFAULT_FORM_DATA };

    // Pre-fill user info (only for "add" mode)
    if (mode === "add" && user) {
      initial.contactInfo = {
        name: user.name || "",
        phone: user.phoneNumber || "",
        email: user.email || "",
      };
    }

    // Pre-fill branch if selected (only for "add" mode)
    if (mode === "add" && selectedBranch) {
      initial.branchCode = selectedBranch;
      branchSetRef.current = true;
    }

    // Pre-fill cart ID if available (only for "add" mode)
    if (mode === "add" && cart?._id) {
      initial.cartId = cart._id;
      cartSetRef.current = true;
    }

    // ✅ CRITICAL FIX: Only mark as initialized for ADD mode
    // In RESCHEDULE mode, we need initializedRef to stay FALSE
    // so the useEffect can populate the form from appointment data
    if (mode === "add") {
      initializedRef.current = true;
    }

    return initial;
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [loadingAppointment, setLoadingAppointment] = useState(false); // ✅ Loading state

  // ✅ FETCH EXISTING APPOINTMENT DATA FOR RESCHEDULE MODE
  useEffect(() => {
    if (
      mode === "reschedule" &&
      appointmentId &&
      !appointmentLoadedRef.current
    ) {
      console.log(
        "🔄 [RESCHEDULE] Fetching appointment data for ID:",
        appointmentId
      );
      console.log(
        "🔄 [RESCHEDULE] Current initializedRef:",
        initializedRef.current
      );
      setLoadingAppointment(true);

      dispatch(getAppointmentDetailsThunk(appointmentId))
        .unwrap()
        .then(() => {
          console.log("✅ [RESCHEDULE] Appointment data fetched successfully");
          appointmentLoadedRef.current = true;
          setLoadingAppointment(false);
        })
        .catch((error) => {
          console.error("❌ [RESCHEDULE] Failed to fetch appointment:", error);
          toast.error("Failed to load appointment details");
          setLoadingAppointment(false);
          navigate("/appointments"); // Redirect back if appointment not found
        });
    }
  }, [mode, appointmentId, dispatch, navigate]);

  // ✅ POPULATE FORM WITH EXISTING APPOINTMENT DATA
  useEffect(() => {
    console.log("🔍 [POPULATE CHECK]", {
      mode,
      hasAppointment: !!selectedAppointment,
      appointmentLoaded: appointmentLoadedRef.current,
      initialized: initializedRef.current,
      shouldPopulate:
        mode === "reschedule" &&
        selectedAppointment &&
        appointmentLoadedRef.current &&
        !initializedRef.current,
    });

    if (
      mode === "reschedule" &&
      selectedAppointment &&
      appointmentLoadedRef.current &&
      !initializedRef.current
    ) {
      console.log(
        "📝 [POPULATE] Starting form population with appointment:",
        selectedAppointment._id
      );

      // ✅ Handle timezone correctly - use local time, not UTC
      const scheduledDate = new Date(selectedAppointment.scheduledDateTime);

      // Get local date components
      const year = scheduledDate.getFullYear();
      const month = String(scheduledDate.getMonth() + 1).padStart(2, "0");
      const day = String(scheduledDate.getDate()).padStart(2, "0");
      const dateStr = `${year}-${month}-${day}`; // YYYY-MM-DD in local time

      // Get local time components
      const hours = String(scheduledDate.getHours()).padStart(2, "0");
      const minutes = String(scheduledDate.getMinutes()).padStart(2, "0");
      const timeStr = `${hours}:${minutes}`; // HH:MM in local time

      console.log("📅 Extracted date/time:", {
        iso: selectedAppointment.scheduledDateTime,
        dateStr,
        timeStr,
        fullDate: scheduledDate.toString(),
      });

      // Map appointment type duration
      const appointmentTypeDurations = {
        PRODUCT_CONSULTATION: 30,
        CUSTOM_DESIGN: 60,
        REPAIR_CONSULTATION: 30,
        APPRAISAL: 45,
        ENGAGEMENT_RING: 60,
        WEDDING_JEWELRY: 60,
        GENERAL_INQUIRY: 20,
        VIP_CLIENT: 90,
      };

      // ✅ Handle user data - can be either object or separate fields
      const userId =
        selectedAppointment.userId?._id || selectedAppointment.userId;
      const userName =
        selectedAppointment.userId?.name || selectedAppointment.userName;
      const userPhone =
        selectedAppointment.userId?.phoneNumber ||
        selectedAppointment.userPhone;
      const userEmail =
        selectedAppointment.userId?.email ||
        selectedAppointment.userEmail ||
        "";

      // ✅ Create selectedCustomer object for display
      const selectedCustomer = {
        _id: userId,
        name: userName,
        phoneNumber: userPhone,
        email: userEmail,
        userCode: selectedAppointment.customerId || "",
      };

      setFormData({
        // User info
        userId: userId,
        selectedCustomer: selectedCustomer,

        // Branch info
        branchCode: selectedAppointment.branchCode || "",
        branchId: selectedAppointment.branchId || "",
        storeCode: selectedAppointment.storeCode || "",
        adminCode: selectedAppointment.adminCode || "",

        // Appointment details
        type: selectedAppointment.type || "",
        selectedDate: dateStr,
        selectedTime: timeStr,
        scheduledDateTime: selectedAppointment.scheduledDateTime,
        duration:
          appointmentTypeDurations[selectedAppointment.type] ||
          selectedAppointment.duration ||
          30,
        cartId: selectedAppointment.cartId || "",

        // Intent details
        intent: {
          purpose: selectedAppointment.intent?.purpose || "",
          occasion: selectedAppointment.intent?.occasion || "",
          occasionDate: selectedAppointment.intent?.occasionDate || "",
          forWhom: selectedAppointment.intent?.forWhom || "",
          budget: {
            min: selectedAppointment.intent?.budget?.min || "",
            max: selectedAppointment.intent?.budget?.max || "",
            flexible: selectedAppointment.intent?.budget?.flexible || false,
          },
          preferences: {
            metals: selectedAppointment.intent?.preferences?.metals || [],
            styles: selectedAppointment.intent?.preferences?.styles || [],
            categories:
              selectedAppointment.intent?.preferences?.categories || [],
            weightRange: {
              min:
                selectedAppointment.intent?.preferences?.weightRange?.min || "",
              max:
                selectedAppointment.intent?.preferences?.weightRange?.max || "",
            },
          },
          customerNotes: selectedAppointment.intent?.customerNotes || "",
          specialRequests: selectedAppointment.intent?.specialRequests || "",
        },

        // Contact info
        contactInfo: {
          name: userName,
          phone: userPhone,
          email: userEmail,
        },
      });

      initializedRef.current = true;
      branchSetRef.current = true;
      console.log("✅ [POPULATE] Form populated successfully!");
      console.log("📊 [POPULATE] Populated data:", {
        userId,
        userName,
        userPhone,
        branchCode: selectedAppointment.branchCode,
        type: selectedAppointment.type,
        date: dateStr,
        time: timeStr,
        duration:
          appointmentTypeDurations[selectedAppointment.type] ||
          selectedAppointment.duration ||
          30,
      });
    } else if (mode === "reschedule") {
      console.log("⏭️ [POPULATE] Skipped - conditions not met");
    }
  }, [mode, selectedAppointment]);

  // ✅ Update branch when global branch changes - ONLY FOR ADD MODE
  useEffect(() => {
    if (mode === "add" && selectedBranch && !branchSetRef.current) {
      setFormData((prev) => {
        // Only update if it's actually different
        if (prev.branchCode !== selectedBranch) {
          branchSetRef.current = true;
          return {
            ...prev,
            branchCode: selectedBranch,
          };
        }
        return prev;
      });
    }
  }, [mode, selectedBranch]);

  // ✅ Update cart ID when cart changes - ONLY FOR ADD MODE
  useEffect(() => {
    if (mode === "add" && cart?._id && !cartSetRef.current) {
      setFormData((prev) => {
        // Only update if it's actually different
        if (prev.cartId !== cart._id) {
          cartSetRef.current = true;
          return {
            ...prev,
            cartId: cart._id,
          };
        }
        return prev;
      });
    }
  }, [mode, cart?._id]);

  // Handle field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle nested field changes (e.g., "intent.purpose")
  const handleNestedChange = (path, value) => {
    const parts = path.split(".");

    setFormData((prev) => {
      const newData = { ...prev };
      let current = newData;

      for (let i = 0; i < parts.length - 1; i++) {
        current[parts[i]] = { ...current[parts[i]] };
        current = current[parts[i]];
      }

      current[parts[parts.length - 1]] = value;
      return newData;
    });

    // Clear error for this field
    if (errors[path]) {
      setErrors((prev) => ({ ...prev, [path]: "" }));
    }
  };

  // Handle array changes
  const handleArrayChange = (path, value) => {
    handleNestedChange(path, value);
  };

  const handleCustomerSelect = (customer) => {
    setFormData((prev) => ({
      ...prev,
      userId: customer._id,
      selectedCustomer: customer,
      contactInfo: {
        name: customer.name || "",
        phone: customer.phoneNumber || "",
        email: customer.email || "",
      },
    }));
  };

  const canProceedToStep = (stepNumber) => {
    const newErrors = {};

    if (stepNumber === 1) {
      if (!formData.userId || !formData.selectedCustomer) {
        newErrors.userId = "Please select a customer";
      }
      if (!formData.branchCode) {
        newErrors.branchCode = "Please select a branch";
      }
      if (!formData.type) {
        newErrors.type = "Please select appointment type";
      }
    }

    if (stepNumber === 2) {
      if (!formData.selectedDate) {
        newErrors.selectedDate = "Please select a date";
      }
      if (!formData.selectedTime) {
        newErrors.selectedTime = "Please select a time slot";
      }

      // ✅ Validate reschedule reason in reschedule mode
      if (mode === "reschedule" && !formData.rescheduleReason?.trim()) {
        newErrors.rescheduleReason = "Please provide a reason for rescheduling";
      }

      if (formData.selectedDate) {
        const selectedDate = new Date(formData.selectedDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDate < today) {
          newErrors.selectedDate = "Cannot book appointments in the past";
        }
      }
    }

    if (stepNumber === 3) {
      if (!formData.intent.purpose) {
        newErrors["intent.purpose"] = "Please select purpose";
      }

      if (formData.intent.budget.min && formData.intent.budget.max) {
        if (
          Number(formData.intent.budget.min) >
          Number(formData.intent.budget.max)
        ) {
          newErrors["intent.budget.max"] =
            "Max budget must be greater than min";
        }
      }
    }

    // ✅ Return boolean only - DO NOT call setErrors
    return Object.keys(newErrors).length === 0;
  };

  // Validate current step
  const validateStep = (stepNumber) => {
    const newErrors = {};

    if (stepNumber === 1) {
      if (!formData.userId || !formData.selectedCustomer) {
        newErrors.userId = "Please select a customer";
      }
      if (!formData.branchCode) {
        newErrors.branchCode = "Please select a branch";
      }
      if (!formData.type) {
        newErrors.type = "Please select appointment type";
      }
    }

    if (stepNumber === 2) {
      if (!formData.selectedDate) {
        newErrors.selectedDate = "Please select a date";
      }
      if (!formData.selectedTime) {
        newErrors.selectedTime = "Please select a time slot";
      }

      // ✅ Validate reschedule reason in reschedule mode
      if (mode === "reschedule" && !formData.rescheduleReason?.trim()) {
        newErrors.rescheduleReason = "Please provide a reason for rescheduling";
      }

      if (formData.selectedDate) {
        const selectedDate = new Date(formData.selectedDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDate < today) {
          newErrors.selectedDate = "Cannot book appointments in the past";
        }
      }
    }

    if (stepNumber === 3) {
      if (!formData.intent.purpose) {
        newErrors["intent.purpose"] = "Please select purpose";
      }

      if (formData.intent.budget.min && formData.intent.budget.max) {
        if (
          Number(formData.intent.budget.min) >
          Number(formData.intent.budget.max)
        ) {
          newErrors["intent.budget.max"] =
            "Max budget must be greater than min";
        }
      }
    }

    // ✅ Only set errors when explicitly validating
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate complete form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.branchCode) {
      newErrors.branchCode = "Branch is required";
    }
    if (!formData.type) {
      newErrors.type = "Appointment type is required";
    }
    if (!formData.selectedDate) {
      newErrors.selectedDate = "Date is required";
    }
    if (!formData.selectedTime) {
      newErrors.selectedTime = "Time is required";
    }
    if (!formData.intent.purpose) {
      newErrors["intent.purpose"] = "Purpose is required";
    }

    if (formData.selectedDate) {
      const selectedDate = new Date(formData.selectedDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        newErrors.selectedDate = "Cannot book appointments in the past";
      }
    }

    if (!formData.contactInfo.phone || formData.contactInfo.phone.length < 10) {
      newErrors["contactInfo.phone"] = "Valid phone number is required";
    }

    if (
      formData.contactInfo.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactInfo.email)
    ) {
      newErrors["contactInfo.email"] = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if can proceed to next step
  const canProceedToNextStep = () => {
    return canProceedToStep(step); // ✅ Uses the version that doesn't call setErrors
  };

  // ✅ HANDLE FORM SUBMISSION - Different logic for add vs reschedule
  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      if (!formData.userId || !formData.selectedCustomer) {
        toast.error("Please select a customer");
        setSubmitting(false);
        return;
      }

      const [hours, minutes] = formData.selectedTime.split(":");
      const scheduledDate = new Date(formData.selectedDate);
      scheduledDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      // ✅ BUILD SUBMIT DATA BASED ON MODE
      if (mode === "reschedule") {
        // ✅ RESCHEDULE MODE - Match backend API expectations
        const rescheduleData = {
          newScheduledDateTime: scheduledDate.toISOString(), // ✅ Backend expects this field name
          reason: formData.rescheduleReason || "Admin rescheduled", // ✅ Use form reason or default
          notifyCustomer: true, // ✅ Send notification to customer
        };

        console.log("📤 Submitting reschedule data:", rescheduleData);

        await dispatch(
          rescheduleAppointmentThunk({
            appointmentId,
            rescheduleData,
          })
        ).unwrap();

        toast.success("Appointment rescheduled successfully!");
        navigate("/appointments");
      } else {
        // ✅ ADD MODE - Create new appointment
        const submitData = {
          userId: formData.userId,
          branchCode: formData.branchCode,
          type: formData.type,
          scheduledDateTime: scheduledDate.toISOString(),
          duration: formData.duration,
          intent: {
            purpose: formData.intent.purpose,
            occasion: formData.intent.occasion || undefined,
            occasionDate: formData.intent.occasionDate || undefined,
            budget: {
              min: Number(formData.intent.budget.min) || undefined,
              max: Number(formData.intent.budget.max) || undefined,
              flexible: formData.intent.budget.flexible,
            },
            preferences: {
              metals: formData.intent.preferences.metals || [],
              styles: formData.intent.preferences.styles || [],
              categories: formData.intent.preferences.categories || [],
            },
            customerNotes: formData.intent.customerNotes || undefined,
          },
        };

        if (formData.cartId) {
          submitData.cartId = formData.cartId;
        }

        // Clean up undefined values
        Object.keys(submitData).forEach((key) => {
          if (submitData[key] === undefined) {
            delete submitData[key];
          }
        });

        console.log("📤 Submitting new appointment data:", submitData);

        await dispatch(adminCreateAppointmentThunk(submitData)).unwrap();

        toast.success("Appointment booked successfully!");
        navigate("/appointments");
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(
        error.message ||
          `Failed to ${
            mode === "reschedule" ? "reschedule" : "book"
          } appointment`
      );
    } finally {
      setSubmitting(false);
    }
  };

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    submitting,
    step,
    setStep,
    handleChange,
    handleNestedChange,
    handleArrayChange,
    handleSubmit,
    validateForm,
    validateStep,
    canProceedToNextStep,
    handleCustomerSelect,
    loadingAppointment,
    mode,
    serverError: error,
  };
};
